/**
 * Bible Mysteries Decoder — the FULL overlay kit, in Concept C's forensic look
 * (the one Yash approved). Analogous roster to jyotirlinga's kit (PersonTag,
 * LocationTag, YearStamp, ScriptureRef, QuoteCard, InfoBox, StatCard, EndCard,
 * CTA) but themed cold/evidence instead of warm/devotional. Shared design
 * tokens below so every component reads as ONE system.
 *
 * Components:
 *  1. WordDecodeLowerThird — the hero reveal moment (letter-decode animation)
 *  2. RootBreakdown        — Hebrew/Greek root letters -> meaning, THE signature shot
 *  3. ScriptureTag         — plain verse citation stamp (high frequency)
 *  4. EntityTag            — named figure or site (Luther / Qumran / Sinaiticus)
 *  5. DateStamp            — year/century marker + context
 *  6. TextualSplit         — "YOUR BIBLE SAYS / THE TEXT SAYS" comparison
 *  7. QuoteCard            — a pulled verse, full text + citation
 *  8. StatCard             — label/value stat callouts
 *  9. ChapterCard          — section-break title card
 * 10. MythBustStamp        — CLAIM / VERDICT debunk device
 * 11. EndCard              — closing card, no CTA button (director LAW)
 *
 * NOT wired into any production — showcase/review preview only.
 */
import { AbsoluteFill, Img, useCurrentFrame, useVideoConfig, interpolate, Easing } from "remotion";
import { loadFont as loadMono } from "@remotion/google-fonts/IBMPlexMono";
import { loadFont as loadSans } from "@remotion/google-fonts/Inter";

const { fontFamily: mono } = loadMono("normal", { weights: ["400", "500", "600"], subsets: ["latin"] });
const { fontFamily: sans } = loadSans("normal", { weights: ["400", "500", "600", "700", "800"], subsets: ["latin"] });

// ─── shared design tokens (the "one system" look) ───
const INK = "#f2f4f6";
const INK_DIM = "#c3cbd2";
const LABEL = "#8b949c";
const LINE = "#9aa7b2";
const PANEL_BG = "rgba(9,13,18,0.86)";
const PANEL_BORDER = "rgba(154,167,178,0.28)";
const ACCENT = "#7fb8a4"; // cool teal — used sparingly for the "answer"/verdict half only

export const BG_URL = "https://pub-bb3ce5df1bc243da96f744a22c134897.r2.dev/beyond_bible/1a_bible_decoder/_style_tests/closeup/kadosh_closeup_3_cinematic_filmstill.jpg";

export const Plate: React.FC<{ children: React.ReactNode; bg?: string }> = ({ children, bg = BG_URL }) => (
  <AbsoluteFill style={{ background: "#000" }}>
    <Img src={bg} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
    {children}
  </AbsoluteFill>
);

const useFadeIn = (delay: number, dur = 14) => {
  const frame = useCurrentFrame();
  return interpolate(frame, [delay, delay + dur], [0, 1], {
    extrapolateLeft: "clamp", extrapolateRight: "clamp", easing: Easing.out(Easing.cubic),
  });
};
const useSlide = (delay: number, dur = 18, from = -16) => {
  const frame = useCurrentFrame();
  return interpolate(frame, [delay, delay + dur], [from, 0], {
    extrapolateLeft: "clamp", extrapolateRight: "clamp", easing: Easing.out(Easing.cubic),
  });
};

const Kicker: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div style={{ fontFamily: sans, fontSize: 12, color: LABEL, letterSpacing: 2.5, textTransform: "uppercase", marginBottom: 6 }}>
    {children}
  </div>
);

// ═══ 1. WORD DECODE LOWER-THIRD (the hero — letter-decode animation) ═══
export const WordDecodeLowerThird: React.FC<{
  word: string; citation: string; startFrame?: number; left?: number; bottom?: number;
}> = ({ word, citation, startFrame = 0, left = 60, bottom = 72 }) => {
  const frame = useCurrentFrame() - startFrame;
  const inA = interpolate(frame, [4, 16], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  const slideX = useSlide(startFrame + 4, 18);
  const decodeStart = 20, decodeDur = 26;
  const revealCount = Math.floor(interpolate(frame, [decodeStart, decodeStart + decodeDur], [0, word.length], { extrapolateLeft: "clamp", extrapolateRight: "clamp" }));
  const glyphs = "XQZKVWY#%&";
  const displayed = word.split("").map((ch, i) => (i < revealCount || ch === " " ? ch : glyphs[(frame + i * 7) % glyphs.length])).join("");
  if (frame < 0) return null;
  return (
    <div style={{ position: "absolute", left, bottom, opacity: inA, transform: `translateX(${slideX}px)` }}>
      <div style={{ fontFamily: mono, fontSize: 34, fontWeight: 500, color: INK, letterSpacing: 3 }}>{displayed}</div>
      <div style={{ width: 64, height: 2, background: LINE, margin: "10px 0" }} />
      <div style={{ fontFamily: sans, fontSize: 15, color: INK_DIM }}>{citation}</div>
    </div>
  );
};

// ═══ 2. ROOT BREAKDOWN (the channel's signature shot — letters -> meaning) ═══
export const RootBreakdown: React.FC<{
  letters: string[]; word: string; meaning: string; startFrame?: number;
}> = ({ letters, word, meaning, startFrame = 0 }) => {
  const frame = useCurrentFrame() - startFrame;
  const inA = interpolate(frame, [4, 16], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  if (frame < 0) return null;
  return (
    <div style={{ position: "absolute", left: 60, top: 90, opacity: inA }}>
      <div style={{ background: PANEL_BG, border: `1px solid ${PANEL_BORDER}`, padding: "20px 26px" }}>
        <Kicker>ROOT</Kicker>
        <div style={{ display: "flex", gap: 10, marginBottom: 10 }}>
          {letters.map((l, i) => {
            const d = interpolate(frame, [10 + i * 8, 18 + i * 8], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
            return (
              <div key={i} style={{
                fontFamily: mono, fontSize: 26, color: INK, opacity: d,
                border: `1px solid ${PANEL_BORDER}`, width: 44, height: 44,
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>{l}</div>
            );
          })}
        </div>
        <div style={{ fontFamily: sans, fontSize: 18, fontWeight: 700, color: INK }}>{word}</div>
        <div style={{ fontFamily: sans, fontSize: 14, color: ACCENT, marginTop: 2 }}>{meaning}</div>
      </div>
    </div>
  );
};

// ═══ 3. SCRIPTURE TAG (plain citation stamp — high frequency) ═══
export const ScriptureTag: React.FC<{ citation: string; startFrame?: number; top?: number }> = ({ citation, startFrame = 0, top = 56 }) => {
  const frame = useCurrentFrame() - startFrame;
  const inA = interpolate(frame, [2, 12], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  if (frame < 0) return null;
  return (
    <div style={{
      position: "absolute", top, right: 60, opacity: inA,
      fontFamily: mono, fontSize: 13, color: INK_DIM, letterSpacing: 1,
      border: `1px solid ${PANEL_BORDER}`, padding: "7px 14px", background: PANEL_BG,
    }}>{citation}</div>
  );
};

// ═══ 4. ENTITY TAG (named figure or site) ═══
export const EntityTag: React.FC<{
  kind: "FIGURE" | "SITE" | "MANUSCRIPT"; name: string; detail: string; startFrame?: number; bottom?: number;
}> = ({ kind, name, detail, startFrame = 0, bottom = 60 }) => {
  const frame = useCurrentFrame() - startFrame;
  const inA = interpolate(frame, [3, 15], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  const slideX = useSlide(startFrame + 3, 16);
  if (frame < 0) return null;
  return (
    <div style={{
      position: "absolute", left: 60, bottom, opacity: inA, transform: `translateX(${slideX}px)`,
      background: "linear-gradient(to right, rgba(9,13,18,0.85) 0%, rgba(9,13,18,0.72) 70%, transparent 100%)",
      padding: "10px 60px 10px 16px", borderLeft: `2px solid ${ACCENT}`,
    }}>
      <div style={{ fontFamily: mono, fontSize: 10, color: ACCENT, letterSpacing: 2 }}>{kind}</div>
      <div style={{ fontFamily: sans, fontSize: 19, fontWeight: 700, color: INK, marginTop: 1 }}>{name}</div>
      <div style={{ fontFamily: sans, fontSize: 12, color: INK_DIM, marginTop: 1 }}>{detail}</div>
    </div>
  );
};

// ═══ 5. DATE STAMP ═══
export const DateStamp: React.FC<{ display: string; context?: string; startFrame?: number }> = ({ display, context, startFrame = 0 }) => {
  const frame = useCurrentFrame() - startFrame;
  const inA = interpolate(frame, [3, 15], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  if (frame < 0) return null;
  return (
    <div style={{ position: "absolute", top: 56, left: 60, opacity: inA }}>
      <div style={{ fontFamily: mono, fontSize: 24, color: INK, letterSpacing: 1 }}>{display}</div>
      {context && <div style={{ fontFamily: sans, fontSize: 12, color: INK_DIM, marginTop: 2, maxWidth: 360 }}>{context}</div>}
    </div>
  );
};

// ═══ 6. TEXTUAL SPLIT ("YOUR BIBLE SAYS / THE TEXT SAYS") ═══
export const TextualSplit: React.FC<{ leftLabel: string; leftVal: string; rightLabel: string; rightVal: string; startFrame?: number }> = ({ leftLabel, leftVal, rightLabel, rightVal, startFrame = 0 }) => {
  const frame = useCurrentFrame() - startFrame;
  const inA = interpolate(frame, [4, 18], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  if (frame < 0) return null;
  return (
    <div style={{
      position: "absolute", left: 60, right: 60, bottom: 64, opacity: inA,
      background: "linear-gradient(180deg, rgba(9,13,18,0.1) 0%, rgba(9,13,18,0.88) 35%)",
      padding: "26px 30px 22px", display: "flex", gap: 40,
    }}>
      <div style={{ flex: 1 }}>
        <Kicker>{leftLabel}</Kicker>
        <div style={{ fontFamily: sans, fontSize: 24, fontWeight: 700, color: INK }}>{leftVal}</div>
      </div>
      <div style={{ width: 1, background: PANEL_BORDER }} />
      <div style={{ flex: 1 }}>
        <Kicker>{rightLabel}</Kicker>
        <div style={{ fontFamily: sans, fontSize: 24, fontWeight: 700, color: ACCENT }}>{rightVal}</div>
      </div>
    </div>
  );
};

// ═══ 7. QUOTE CARD (a pulled verse, full text) ═══
export const QuoteCardForensic: React.FC<{ text: string; citation: string; startFrame?: number }> = ({ text, citation, startFrame = 0 }) => {
  const frame = useCurrentFrame() - startFrame;
  const inA = interpolate(frame, [4, 18], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  if (frame < 0) return null;
  return (
    <div style={{
      position: "absolute", left: 60, right: 60, top: 90, opacity: inA,
      background: PANEL_BG, borderLeft: `2px solid ${LINE}`, padding: "22px 28px",
    }}>
      <div style={{ fontFamily: sans, fontSize: 20, fontStyle: "italic", color: INK, lineHeight: 1.4 }}>&ldquo;{text}&rdquo;</div>
      <div style={{ fontFamily: mono, fontSize: 12, color: INK_DIM, marginTop: 10, letterSpacing: 1 }}>{citation}</div>
    </div>
  );
};

// ═══ 8. STAT CARD ═══
export const StatCardForensic: React.FC<{ stats: { label: string; value: string }[]; startFrame?: number }> = ({ stats, startFrame = 0 }) => {
  const frame = useCurrentFrame() - startFrame;
  const inA = interpolate(frame, [4, 16], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  if (frame < 0) return null;
  return (
    <div style={{ position: "absolute", top: 56, right: 60, opacity: inA, display: "flex", gap: 22, background: PANEL_BG, border: `1px solid ${PANEL_BORDER}`, padding: "14px 22px" }}>
      {stats.map((s, i) => (
        <div key={i}>
          <div style={{ fontFamily: mono, fontSize: 22, color: INK, fontWeight: 500 }}>{s.value}</div>
          <div style={{ fontFamily: sans, fontSize: 10, color: LABEL, letterSpacing: 1.5, textTransform: "uppercase", marginTop: 2 }}>{s.label}</div>
        </div>
      ))}
    </div>
  );
};

// ═══ 9. CHAPTER CARD (section-break title) ═══
export const ChapterCard: React.FC<{ title: string; startFrame?: number }> = ({ title, startFrame = 0 }) => {
  const frame = useCurrentFrame() - startFrame;
  const inA = interpolate(frame, [0, 16], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  const dim = interpolate(frame, [0, 10], [0, 0.55], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  if (frame < 0) return null;
  return (
    <AbsoluteFill style={{ opacity: inA, background: `rgba(4,6,9,${dim})`, alignItems: "center", justifyContent: "center" }}>
      <div style={{ textAlign: "center" }}>
        <div style={{ fontFamily: mono, fontSize: 12, color: ACCENT, letterSpacing: 4, textTransform: "uppercase", marginBottom: 12 }}>THE EVIDENCE</div>
        <div style={{ fontFamily: sans, fontSize: 40, fontWeight: 800, color: INK, letterSpacing: 1 }}>{title}</div>
        <div style={{ width: 80, height: 2, background: LINE, margin: "18px auto 0" }} />
      </div>
    </AbsoluteFill>
  );
};

// ═══ 10. MYTH-BUST STAMP (CLAIM / VERDICT) ═══
export const MythBustStamp: React.FC<{ claim: string; verdict: string; startFrame?: number }> = ({ claim, verdict, startFrame = 0 }) => {
  const frame = useCurrentFrame() - startFrame;
  const inA = interpolate(frame, [4, 16], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  if (frame < 0) return null;
  return (
    <div style={{ position: "absolute", left: 60, bottom: 72, opacity: inA, background: PANEL_BG, border: `1px solid ${PANEL_BORDER}`, padding: "18px 24px", maxWidth: 560 }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 8 }}>
        <span style={{ fontFamily: mono, fontSize: 11, color: "#c98a6b", letterSpacing: 2 }}>CLAIM</span>
        <span style={{ fontFamily: sans, fontSize: 16, color: INK_DIM, textDecoration: "line-through", textDecorationColor: "#c98a6b" }}>{claim}</span>
      </div>
      <div style={{ display: "flex", alignItems: "baseline", gap: 10 }}>
        <span style={{ fontFamily: mono, fontSize: 11, color: ACCENT, letterSpacing: 2 }}>RECORD</span>
        <span style={{ fontFamily: sans, fontSize: 18, color: INK, fontWeight: 700 }}>{verdict}</span>
      </div>
    </div>
  );
};

// ═══ 11. END CARD (no CTA button — director LAW) ═══
export const EndCardForensic: React.FC<{ episodeTitle: string; startFrame?: number }> = ({ episodeTitle, startFrame = 0 }) => {
  const frame = useCurrentFrame() - startFrame;
  const inA = interpolate(frame, [0, 20], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  if (frame < 0) return null;
  return (
    <AbsoluteFill style={{ opacity: inA, background: "rgba(4,6,9,0.75)", alignItems: "center", justifyContent: "center" }}>
      <div style={{ textAlign: "center" }}>
        <div style={{ fontFamily: mono, fontSize: 13, color: LABEL, letterSpacing: 4, textTransform: "uppercase", marginBottom: 14 }}>BIBLE MYSTERIES DECODER</div>
        <div style={{ fontFamily: sans, fontSize: 26, fontWeight: 700, color: INK, maxWidth: 640 }}>{episodeTitle}</div>
      </div>
    </AbsoluteFill>
  );
};

// ═══════════════════════════════════════════════════════════════════════
// SHOWCASE PREVIEWS — grouped so review takes 3 stills, not 11
// ═══════════════════════════════════════════════════════════════════════

export const Showcase_RevealBeats: React.FC = () => (
  <Plate>
    <RootBreakdown letters={["ק", "ד", "ש"]} word="qadosh" meaning="cut off, set apart" startFrame={0} />
    <WordDecodeLowerThird word="EL SHADDAI" citation="Exodus 6:3 · the name the fathers knew" startFrame={0} />
  </Plate>
);

export const Showcase_InfoBeats: React.FC = () => (
  <Plate>
    <DateStamp display="180 AD" context="Irenaeus names and rejects the Gospel of Thomas" startFrame={0} />
    <ScriptureTag citation="Deuteronomy 32:8" startFrame={0} top={140} />
    <StatCardForensic stats={[{ label: "manuscripts", value: "5,800+" }, { label: "oldest copy", value: "125 AD" }]} startFrame={0} />
    <EntityTag kind="MANUSCRIPT" name="Codex Sinaiticus" detail="4th century, uncial Greek, four columns" startFrame={0} />
  </Plate>
);

export const Showcase_SpecialBeats: React.FC = () => (
  <Plate>
    <TextualSplit leftLabel="YOUR BIBLE SAYS" leftVal="El Shaddai" rightLabel="GOD SAYS" rightVal="Not by my name YHWH" startFrame={0} />
  </Plate>
);

export const Showcase_QuoteAndMyth: React.FC = () => (
  <Plate>
    <QuoteCardForensic text="I appeared to Abraham, to Isaac, and to Jacob as God Almighty, but by my name the Lord I did not make myself known to them." citation="Exodus 6:3 · WEB" startFrame={0} />
  </Plate>
);

export const Showcase_MythBust: React.FC = () => (
  <Plate>
    <MythBustStamp claim="Elohim's plural form proves the Trinity" verdict="Hebrew plurals of majesty are ordinary grammar — not a hidden code" startFrame={0} />
  </Plate>
);

export const Showcase_Chapter: React.FC = () => <ChapterCard title="THE REVEAL" startFrame={0} />;
export const Showcase_EndCard: React.FC = () => <EndCardForensic episodeTitle="Where Did God Come From?" startFrame={0} />;

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
